import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  noindex?: boolean;

  /**
   * 페이지별 추가 schema.
   *
   * 예:
   * FAQPage
   * Article
   * ItemList
   * SoftwareApplication
   */
  structuredData?:
    | Record<string, unknown>
    | Array<Record<string, unknown>>;

  /**
   * Open Graph type.
   */
  type?: 'website' | 'article';
}

const BASE_URL = 'https://www.policy-intel.com';

const SITE_NAME = '폴리시인텔';

const DEFAULT_IMAGE =
  `${BASE_URL}/images/policy-intel.png`;

const ORGANIZATION_ID =
  `${BASE_URL}/#organization`;

const WEBSITE_ID =
  `${BASE_URL}/#website`;

/**
 * canonical에 사용할 path를 정규화한다.
 *
 * 지원 입력:
 *
 * /
 * /stocks
 * /stocks/
 * stocks
 * https://www.policy-intel.com/stocks
 * /stocks?symbol=005930
 * /stocks#chart
 */
function normalizeCanonicalPath(
  path: string,
): string {
  const raw = (path || '/').trim();

  let pathname = raw;

  /**
   * 절대 URL이 전달된 경우 pathname만 추출한다.
   */
  if (/^https?:\/\//i.test(raw)) {
    try {
      pathname = new URL(raw).pathname;
    } catch {
      pathname = '/';
    }
  }

  /**
   * query/hash 제거.
   */
  pathname =
    pathname.split(/[?#]/, 1)[0] || '/';

  /**
   * leading slash 보장.
   */
  if (!pathname.startsWith('/')) {
    pathname = `/${pathname}`;
  }

  /**
   * 중복 slash 제거.
   *
   * //stocks///005930
   * →
   * /stocks/005930
   */
  pathname =
    pathname.replace(/\/{2,}/g, '/');

  /**
   * root를 제외하고 trailing slash 제거.
   */
  if (pathname !== '/') {
    pathname =
      pathname.replace(/\/+$/, '');
  }

  return pathname || '/';
}

/**
 * 상대 이미지 URL을 절대 URL로 변환한다.
 */
function normalizeAbsoluteUrl(
  value: string,
): string {
  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalized =
    value.startsWith('/')
      ? value
      : `/${value}`;

  return `${BASE_URL}${normalized}`;
}

/**
 * JSON-LD 안에 </script> 등이 들어가
 * script가 조기 종료되는 것을 방지한다.
 */
function safeJsonLd(
  value: unknown,
): string {
  return JSON
    .stringify(value)
    .replace(/</g, '\\u003c');
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  structuredData,
  type = 'website',
}) => {
  const canonicalPath =
    normalizeCanonicalPath(path);

  const canonicalUrl =
    `${BASE_URL}${canonicalPath}`;

  const imageUrl =
    normalizeAbsoluteUrl(image);

  const robotsContent =
    noindex
      ? 'noindex,nofollow'
      : [
          'index',
          'follow',
          'max-image-preview:large',
          'max-snippet:-1',
          'max-video-preview:-1',
        ].join(',');

  const yetiContent =
    noindex
      ? 'noindex,nofollow'
      : [
          'index',
          'follow',
          'max-image-preview:large',
          'max-snippet:-1',
        ].join(',');

  const additionalSchemas =
    structuredData
      ? (
          Array.isArray(structuredData)
            ? structuredData
            : [structuredData]
        )
      : [];

  /**
   * 사이트 전체에서 공통으로 사용하는
   * Organization schema.
   */
  const organizationSchema: Record<
    string,
    unknown
  > = {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,

    name: SITE_NAME,

    alternateName: [
      'PolicyIntel',
      'Policy Intel Korea',
    ],

    url: `${BASE_URL}/`,

    description:
      '시장 방향과 보유종목의 가격·수급·재무·가치·공시 변화를 연결해 현재 상태와 변화 근거를 제공하는 국내주식 분석 서비스',

    logo: {
      '@type': 'ImageObject',
      '@id': `${BASE_URL}/#logo`,
      url: DEFAULT_IMAGE,
      contentUrl: DEFAULT_IMAGE,
      width: 512,
      height: 512,
      caption: SITE_NAME,
    },

    image: {
      '@id': `${BASE_URL}/#logo`,
    },
  };

  /**
   * 사이트 전체 WebSite schema.
   */
  const websiteSchema: Record<
    string,
    unknown
  > = {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,

    url: `${BASE_URL}/`,

    name: SITE_NAME,

    alternateName: 'PolicyIntel',

    description:
      '보유종목 변화, 시장 방향, 재무·가치, 기업 공시·정책 이슈, 차트와 스크리너를 연결하는 국내주식 분석 서비스',

    inLanguage: 'ko-KR',

    publisher: {
      '@id': ORGANIZATION_ID,
    },
  };

  /**
   * 현재 route의 WebPage schema.
   *
   * canonical URL을 기준으로 생성되므로
   * /stocks, /issue, /screener 등이
   * 서로 다른 페이지로 인식된다.
   */
  const webPageSchema: Record<
    string,
    unknown
  > = {
    '@type': 'WebPage',

    '@id':
      `${canonicalUrl}#webpage`,

    url: canonicalUrl,

    name: title,

    description,

    inLanguage: 'ko-KR',

    isPartOf: {
      '@id': WEBSITE_ID,
    },

    about: {
      '@id': ORGANIZATION_ID,
    },

    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: imageUrl,
    },
  };

  const graph = [
    organizationSchema,
    websiteSchema,
    webPageSchema,
    ...additionalSchemas,
  ];

  const jsonLd = safeJsonLd({
    '@context': 'https://schema.org',
    '@graph': graph,
  });

  return (
    <Helmet>
      <html lang="ko"/>

      <title>{title}</title>

      <meta
        name="description"
        content={description}
      />

      <meta
        name="robots"
        content={robotsContent}
      />

      <meta
        name="googlebot"
        content={robotsContent}
      />

      <meta
        name="Yeti"
        content={yetiContent}
      />

      <link
        rel="canonical"
        href={canonicalUrl}
      />

      {/* Open Graph */}

      <meta
        property="og:type"
        content={type}
      />

      <meta
        property="og:locale"
        content="ko_KR"
      />

      <meta
        property="og:site_name"
        content={SITE_NAME}
      />

      <meta
        property="og:title"
        content={title}
      />

      <meta
        property="og:description"
        content={description}
      />

      <meta
        property="og:url"
        content={canonicalUrl}
      />

      <meta
        property="og:image"
        content={imageUrl}
      />

      <meta
        property="og:image:secure_url"
        content={imageUrl}
      />

      <meta
        property="og:image:alt"
        content={`${title} 대표 이미지`}
      />

      {/* Twitter */}

      <meta
        name="twitter:card"
        content="summary"
      />

      <meta
        name="twitter:title"
        content={title}
      />

      <meta
        name="twitter:description"
        content={description}
      />

      <meta
        name="twitter:image"
        content={imageUrl}
      />

      <meta
        name="twitter:image:alt"
        content={`${title} 대표 이미지`}
      />

      {/* JSON-LD */}

      <script
        type="application/ld+json"
      >
        {jsonLd}
      </script>
    </Helmet>
  );
};

export default SEO;
